import localForage from 'localforage';
import { CadenceType } from '@repo/utils/models/Cadence';
import { requestActions } from './apiHelpers';
import { catchError, delay, map, mergeMap, Observable, of, startWith } from 'rxjs';
import { ReduxAction } from '@repo/utils/types';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import AppState from '../reducers/app-state';
import { apiObservable } from '@repo/utils/helpers/api';
import Occasion from '@repo/utils/helpers/Occasion';
import { fetchGroups, selectGroup, selectGroupCollection } from './groups';
import { chunkArray } from '@repo/utils/helpers/array';

const csvImportActions = requestActions('CSV_IMPORT', ['create']);
const csvImportUploadActions = requestActions('CSV_IMPORT_UPLOAD', ['create']);
const importStatusActions = requestActions('IMPORT_STATUS', ['fetch']);

// If we fail 3 times to upload a chunk, that chunk gets stashed locally
const retriedImportUploadFailed = (payload) => ({
  type: 'RETRIED_CSV_IMPORT_UPLOAD_FAILED',
  payload,
});

export interface DateParts {
  year?: number;
  month: number;
  day: number;
}

interface WorkExperience {
  title?: string;
  company?: string;
}

interface Address {
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface ImportablePerson {
  // Lookup fields. Need to have at least one of these (or both First Name and Last Name together) for a valid row
  name?: string;
  first_name?: string;
  last_name?: string;
  email_addresses?: string[];
  phone_numbers?: string[];
  linkedin_urls?: string[];
  facebook_urls?: string[];
  twitter_urls?: string[];
  // all other supported, optional fields
  notes?: string[];
  groups?: string[];
  birthday?: DateParts;
  work_history?: WorkExperience[];
  websites?: string[];
  headline?: string;
  cadence?: CadenceType;
  addresses?: Address[];
}

export const createCSVImport = ({
  rows,
  group_names,
  localImportID,
}: {
  rows: ImportablePerson[];
  group_names: string[];
  localImportID: string;
}) => csvImportActions.create({ rows, group_names, localImportID });

// Creates the import record in the API. Contact upload is handled after this in a
// separate epic (uploadImportEpic).
const createCSVImportEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType('CREATE_CSV_IMPORT'),
    mergeMap(({ payload }) => {
      const dateDisplay = new Occasion().format('y-MM-dd');
      const baseName = `CSV Import - ${dateDisplay}`;
      let name = baseName;
      const groups = selectGroupCollection(state$.value).items.valueSeq().toArray();
      const groupsImportedToday = groups.filter((group) => group.title.startsWith(name));

      if (groupsImportedToday.length > 0) {
        let count = groupsImportedToday.length;
        name = `${baseName} (${groupsImportedToday.length})`;

        while (groupsImportedToday.find((group) => group.title === name)) {
          count++;
          name = `${baseName} (${count})`;
        }
      }

      return apiObservable(
        'POST',
        '/integrations/bulk-import/',
        {
          name,
          total: payload.rows.length,
          group_names: payload.group_names,
        },
        { apiVersion: 'v2' },
      ).pipe(
        map(({ response }) =>
          csvImportActions.createSucceeded({
            ...response,
            rows: payload.rows,
            localImportID: payload.localImportID,
          }),
        ),
        startWith(csvImportActions.createRequested(payload)),
        catchError((error) =>
          of(csvImportActions.createFailed({ error, localImportID: payload.localImportID })),
        ),
      );
    }),
  );

// Takes the rows from the import and chunks them into smaller groups for upload
const initiateCSVImportUploadEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CSV_IMPORT_SUCCEEDED'),
    mergeMap(({ payload }) => {
      // for now we will chunk by 1,000. we can get more sophisticated with https://github.com/miktam/sizeof if we need to
      const chunks = chunkArray<ImportablePerson>(payload.rows, 1000);

      if (!chunks || !payload.uuid) {
        return of({ type: 'NO_OP' });
      }

      return of(
        ...chunks.map((contacts) =>
          csvImportUploadActions.create({ importId: payload.uuid, contacts }),
        ),
      );
    }),
  );

// TODO: handle retries and ideally handle failures in the UI messaging. This will require some coordination
// with the API such that we can move the import status to a failed state, otherwise we will endlessly poll.
const uploadCSVImportChunkEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CSV_IMPORT_UPLOAD'),
    mergeMap(({ payload }) =>
      apiObservable(
        'POST',
        `/integrations/bulk-import/${payload.importId}/contacts/`,
        { contacts: payload.contacts },
        { apiVersion: 'v2' },
      ).pipe(
        map(({ response }) => csvImportUploadActions.createSucceeded(response)),
        startWith(csvImportUploadActions.createRequested(payload)),
        catchError((error) => of(csvImportUploadActions.createFailed({ error, ...payload }))),
      ),
    ),
  );

const retryUploadCSVImportChunkEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CSV_IMPORT_UPLOAD_FAILED'),
    mergeMap(({ payload }) => {
      if (payload.retryCount > 3) {
        return of(retriedImportUploadFailed(payload));
      }

      return of(
        csvImportUploadActions.create({ ...payload, retryCount: (payload.retryCount || 0) + 1 }),
      );
    }),
  );

// Fetch newly created groups for display in the nav sidebar
// This will also trigger polling for import status for the main "import" group (see next epic)
const fetchCSVImportGroupsEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> =>
  action$.pipe(
    ofType('CREATE_CSV_IMPORT_SUCCEEDED'),
    map(() => fetchGroups({ omitContactIDs: true, isBackgroundSync: true })),
  );

// when groups have been fetched, poll for import status on any group that has an import status that is not 'completed'
// on 'completed', do a background sync fetch of all groups for nav
const startPollingForImportStatusEpic = (
  action$: Observable<ReduxAction>,
): Observable<ReduxAction> =>
  action$.pipe(
    ofType('FETCH_ALL_GROUPS_SUCCEEDED'),
    mergeMap(({ payload }) => {
      const uuidsToPoll: string[][] =
        payload.response
          ?.filter(
            (group) => !!group.bulk_import?.status && group.bulk_import.status !== 'completed',
          )
          .map((group) => [group.bulk_import.uuid, String(group.id)]) || [];

      if (uuidsToPoll.length === 0) {
        return of({ type: 'NO_OP' });
      }

      return of(
        ...uuidsToPoll.map(([uuid, groupID]) => importStatusActions.fetch({ uuid, groupID })),
      );
    }),
  );

const pollForImportStatusEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_IMPORT_STATUS'),
    mergeMap(({ payload }) => {
      // If the group associated with an import has been deleted, stop polling
      const group = selectGroup(payload.groupID, null)(state$.value);

      return apiObservable(
        'GET',
        `/integrations/bulk-import/${payload.uuid}/`,
        {},
        { apiVersion: 'v2' },
      ).pipe(
        delay(1000 * 15), // every 15 seconds
        mergeMap(({ response }) => {
          if (!group) {
            return of({ type: 'NO_OP' });
          }

          if (response?.status && response.status !== 'completed') {
            return of(importStatusActions.fetch({ uuid: payload.uuid, groupID: payload.groupID }));
          }

          return of(importStatusActions.fetchSucceeded(response));
        }),
      );
    }),
  );

export const failedCSVImportStorageKey = 'clay_failed_imports';

export const logOrStashCSVUploadError = (_store) => (next) => (action: ReduxAction) => {
  if (action.type === 'RETRIED_CSV_IMPORT_UPLOAD_FAILED') {
    const importID = action.payload?.importId;
    const failedContacts = action.payload?.contacts;

    if (window.desktopIntegrations) {
      window.desktopIntegrations.writeToLog?.(`
Failed to upload contacts chunk for import ID: ${importID}
Contacts: ${JSON.stringify(failedContacts)}
      `);
    } else {
      const updateStoredFailedContacts = async () => {
        const storedFailedUploads =
          (await localForage.getItem<{}>(failedCSVImportStorageKey)) || {};
        const storedFailedUploadsForImport = storedFailedUploads[importID] || [];
        await localForage.setItem(failedCSVImportStorageKey, {
          ...storedFailedUploads,
          [importID]: [...storedFailedUploadsForImport, failedContacts],
        });
      };

      updateStoredFailedContacts();
    }
  }

  return next(action);
};

export const csvImportEpics = combineEpics(
  createCSVImportEpic,
  initiateCSVImportUploadEpic,
  uploadCSVImportChunkEpic,
  retryUploadCSVImportChunkEpic,
  fetchCSVImportGroupsEpic,
  startPollingForImportStatusEpic,
  pollForImportStatusEpic,
);
